import { apiServer } from "../config";
import type {
  PlacesCityParams,
  PlacesCityResponse,
  PlacesIndexResponse,
} from "../types/places";

/** The city (or the page within it) does not exist. */
export class PlacesNotFoundError extends Error {
  constructor(what: string) {
    super(`Place not found: ${what}`);
    this.name = "PlacesNotFoundError";
  }
}

/** The places service could not answer (5xx or network); never a reason to 404 a page. */
export class PlacesUnavailableError extends Error {
  constructor(status: number) {
    super(`Places are unavailable (${status})`);
    this.name = "PlacesUnavailableError";
  }
}

// Plain fetch, not fetchWithToken: these are public pages, and fetchWithToken returns
// nothing without a signed-in user, which is always the case during SSR.
async function getJson<T>(url: string, what: string): Promise<T> {
  const response = await fetch(url, { headers: { Accept: "application/json" } });
  if (response.status === 404 || response.status === 422) {
    throw new PlacesNotFoundError(what);
  }
  if (!response.ok) {
    throw new PlacesUnavailableError(response.status);
  }
  return response.json();
}

/**
 * Cities with at least the minimum number of venues, grouped by country.
 *
 * @returns The /places index.
 */
export function fetchPlacesIndex(): Promise<PlacesIndexResponse> {
  return getJson<PlacesIndexResponse>(`${apiServer}/business/places`, "places");
}

/**
 * One page of a city, or of one of its types, areas or area types, with its filter sections and curated lists.
 *
 * @param citySlug City slug.
 * @param segments Up to two path segments under the city: a type, an area, or an area then a type.
 * @param params Page and filters.
 * @returns The page data.
 */
export function fetchPlacesPage(
  citySlug: string,
  segments: string[],
  params: PlacesCityParams = {},
): Promise<PlacesCityResponse> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, String(value));
    }
  }
  const search = query.toString();
  const path = [citySlug, ...segments].map(encodeURIComponent).join("/");

  return getJson<PlacesCityResponse>(
    `${apiServer}/business/places/${path}${search ? `?${search}` : ""}`,
    path,
  );
}

/**
 * One page of a city's venues with its filter sections and curated lists.
 *
 * @param slug City slug.
 * @param params Page and filters.
 * @returns The city page data.
 */
export function fetchPlacesCity(
  slug: string,
  params: PlacesCityParams = {},
): Promise<PlacesCityResponse> {
  return fetchPlacesPage(slug, [], params);
}
